import { useCallback, useMemo, useState } from 'react'
import { useIsMobileLg, useIsMobileXl } from 'shared/hooks/useBreakpoint'
import { parseServerInstant } from 'shared/parseServerInstant'

const getLastActivityMeta = (iso, isShot) => {
	if (!iso) {
		return { label: '', variant: 'none', icon: null }
	}

	const date = parseServerInstant(iso)
	if (!date || Number.isNaN(date.getTime())) {
		return { label: '', variant: 'none', icon: null }
	}

	const now = new Date()
	const diffMs = now.getTime() - date.getTime()
	const diffMinutes = Math.floor(diffMs / (60 * 1000))

	const isSameDay =
		now.getFullYear() === date.getFullYear() &&
		now.getMonth() === date.getMonth() &&
		now.getDate() === date.getDate()

	if (diffMinutes < 60) {
		const minutes = Math.max(diffMinutes, 1)
		return {
			label: `${isShot ? '' : 'Online '}${minutes} mins ago`,
			variant: 'recent',
			icon: 'clock',
		}
	}

	if (isSameDay) {
		const time = date.toLocaleTimeString('en-US', {
			hour: 'numeric',
			minute: '2-digit',
		})
		return {
			label: time,
			variant: 'today',
			icon: null,
		}
	}

	const isSameYear = now.getFullYear() === date.getFullYear()
	const label = date.toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: isSameYear ? undefined : 'numeric',
	})

	return {
		label,
		variant: 'old',
		icon: 'laptop',
	}
}

export const useUserListMessage = ({
	users,
	selectedId,
	onSelectUser,
	isLoading,
}) => {
	const [search, setSearch] = useState('')
	const [isCollapsed, setIsCollapsed] = useState(false)
	const [isPinnedOpen, setIsPinnedOpen] = useState(false)
	const [isHovering, setIsHovering] = useState(false)
	const isMobileLg = useIsMobileLg()
	const isMobileXl = useIsMobileXl()

	// users from API: first with conversations (by last message DESC), then without
	const itemsWithMeta = useMemo(
		() =>
			(users || []).map(user => ({
				...user,
				activityMeta: getLastActivityMeta(user.lastActiveAt, isMobileXl),
			})),
		[users, isMobileXl],
	)

	// Same order as API: with conversations (by last message, newest first), then without
	const items = useMemo(() => {
		if (!search.trim()) {
			return itemsWithMeta
		}

		const query = search.toLowerCase().trim()
		return itemsWithMeta.filter(item => {
			const name = String(item.name || '').toLowerCase()
			const email = String(item.email || '').toLowerCase()
			const lastMessage = String(item.lastMessage || '').toLowerCase()
			return (
				name.includes(query) ||
				email.includes(query) ||
				lastMessage.includes(query)
			)
		})
	}, [itemsWithMeta, search])

	const handleUserClick = useCallback(
		user => {
			if (onSelectUser) {
				onSelectUser(user)
			}
		},
		[onSelectUser],
	)

	const handleBackClick = useCallback(() => {
		setIsCollapsed(true)
		setIsPinnedOpen(false)
		setIsHovering(false)
	}, [])

	const handleMouseEnter = useCallback(() => {
		if (isCollapsed && !isPinnedOpen) {
			setIsHovering(true)
		}
	}, [isCollapsed, isPinnedOpen])

	const handleMouseLeave = useCallback(() => {
		if (isCollapsed && !isPinnedOpen) {
			setIsHovering(false)
		}
	}, [isCollapsed, isPinnedOpen])

	const handleOpenClick = useCallback(() => {
		if (!isCollapsed) {
			return
		}
		setIsPinnedOpen(true)
		setIsHovering(false)
	}, [isCollapsed])

	const isExpanded = useMemo(() => {
		if (!isCollapsed) {
			return true
		}
		if (isPinnedOpen) {
			return true
		}
		return isHovering
	}, [isCollapsed, isPinnedOpen, isHovering])

	const showSearch = isExpanded
	const showOpenButton = isCollapsed && !isPinnedOpen

	return {
		items,
		selectedId,
		search,
		setSearch,
		isCollapsed,
		isExpanded,
		showSearch,
		showOpenButton,
		handleUserClick,
		handleBackClick,
		handleMouseEnter,
		handleMouseLeave,
		handleOpenClick,
		isMobileLg,
	}
}
